/**
 * GuideContext — Lykke in the top bar: one conversation that never starts or ends, by chat or by voice, that moves
 * the app for you.
 *
 * It lives above the page switch (App), so neither the thread nor a running call notices a page change: the router
 * swaps the page underneath while the voice keeps talking. Pages tell the guide what is on screen and what they can
 * do (`useAgentPage`); the backend picks the page (Jev) and fills it (Claude); this provider executes: it
 * navigates, runs page actions (asking first when the action says so), and speaks the answer.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { parseAppPath } from '@/routes';
import { API_BASE_URL, authHeaders, coworkApi } from '@/cowork/omniad/api/coworkApi';
import { RealtimeEars } from '@/cowork/proposals/realtimeCall';
import { voiceRefusalText } from '@/cowork/voice/voiceSession';
import { readActiveBrandId } from '@/cowork/useActiveBrand';
import { entryFor, GUIDE_PAGES, mapFor, pathFor, type Viewer } from './pageMap';
import { guideTurn, type GuideEvent, type GuideHereWire } from './guideApi';

export interface GuideAction {
  name: string;
  does: string;
  args?: Record<string, string>;
  /** Ask the person before running it (changes, sends, approvals). */
  confirm?: boolean;
  /** The question shown/spoken before running ("Approve 3 drafts?"). */
  confirmText?: (args: Record<string, unknown>) => string;
  run: (args: Record<string, unknown>) => unknown | Promise<unknown>;
}
export interface PageRegistration { id: string; context?: unknown; actions?: GuideAction[] }

export interface GuideMessage {
  id: string;
  role: 'user' | 'lykke' | 'nav' | 'error';
  text: string;
  via?: 'chat' | 'voice';
  /** nav: where it went and where it came from (Undo); back = this nav was an Undo. */
  path?: string; from?: string; back?: boolean;
}
export type CallState = 'off' | 'connecting' | 'listening' | 'thinking' | 'speaking';
export interface GuideTiming { routeMs?: number; firstNavMs?: number; doneMs?: number }
export interface GuideTurnRecord {
  ask: string; startedAt: number; route?: { page: string; p: number; aboutScreen?: number };
  navigations: Array<{ page: string; path: string; by: string; ms: number; inputs?: Record<string, unknown>; params?: Record<string, unknown> }>;
  confirms?: string[];
  actions: Array<{ action: string; ok: boolean }>;
  said?: string; spoken?: string; timing: GuideTiming; error?: string;
}

/** Test runner knobs (Dev mode → Guide tests): its own thread, no decks built, every "Approve…?" answered no. */
export interface SendOptions { session?: string; dryRun?: boolean; declineConfirms?: boolean }

interface GuideValue {
  open: boolean; setOpen: (v: boolean) => void;
  messages: GuideMessage[]; busy: boolean; working: string | null;
  /** Where Lykke is heading while she works (the router's pick), e.g. "Campaign results". */
  heading: string | null;
  send: (text: string, via?: 'chat' | 'voice', opts?: SendOptions) => Promise<GuideTurnRecord>;
  call: CallState; startCall: () => Promise<void>; endCall: () => void; stopSpeaking: () => void;
  confirmAsk: { text: string } | null; answerConfirm: (yes: boolean) => void;
  undo: (path: string) => void;
  register: (reg: PageRegistration) => () => void;
  available: boolean;
  lastTurn: GuideTurnRecord | null;
}

const Ctx = createContext<GuideValue | null>(null);
const SESSION_KEY = 'guide-session-v1';
const THREAD_KEY = 'guide-thread-v1';
export const VIDEO_HANDOFF_KEY = 'guide-handoff:video';
const uid = () => Math.random().toString(36).slice(2, 10);
const sessionId = () => { try { let s = localStorage.getItem(SESSION_KEY); if (!s) { s = `g-${uid()}${uid()}`; localStorage.setItem(SESSION_KEY, s); } return s; } catch { return 'main'; } };
const loadThread = (): GuideMessage[] => { try { return JSON.parse(sessionStorage.getItem(THREAD_KEY) || '[]'); } catch { return []; } };
const YES = /^(yes|yeah|yep|yup|do it|go ahead|go|confirm|sure|ok(ay)?|ja|japp|jo|kör|gör det)\b/i;
const NO = /^(no|nope|stop|cancel|don'?t|nej|nei|avbryt)\b/i;

/** Lines about money the guide must never repeat (house rule: no costs, fees, spend or CPM for clients). */
const COST_LINE = /\b(spend|spent|cost|costs|cpm|cpc|cpv|cpe|cpa|fee|fees|price|prices|pricing|invest(ment)?|emv|roi|roas)\b/i;
/** The visible text of the page area — what the person is looking at — without the money lines, capped. */
export function readScreen(max = 4000): string {
  const el = document.querySelector<HTMLElement>('[data-guide-screen]');
  if (!el) return '';
  const lines = (el.innerText || '').split('\n').map((l) => l.replace(/\s+/g, ' ').trim()).filter((l) => l && !COST_LINE.test(l));
  const out: string[] = [];
  let n = 0;
  for (const l of lines) { if (n + l.length > max) break; if (out[out.length - 1] !== l) { out.push(l); n += l.length + 1; } }
  return out.join('\n');
}

export function GuideProvider({ children, viewer, enabled }: { children: ReactNode; viewer: Viewer; enabled: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<GuideMessage[]>(loadThread);
  const [busy, setBusy] = useState(false);
  const [working, setWorking] = useState<string | null>(null);
  const [heading, setHeading] = useState<string | null>(null);
  const [call, setCall] = useState<CallState>('off');
  const [confirmAsk, setConfirmAsk] = useState<{ text: string } | null>(null);
  const [lastTurn, setLastTurn] = useState<GuideTurnRecord | null>(null);
  const confirmResolve = useRef<((yes: boolean) => void) | null>(null);
  const regs = useRef<PageRegistration[]>([]);
  const ears = useRef<RealtimeEars | null>(null);
  const audio = useRef<HTMLAudioElement | null>(null);
  const callRef = useRef<CallState>('off');
  const locRef = useRef(location);
  locRef.current = location;
  const viewerRef = useRef(viewer);
  viewerRef.current = viewer;
  callRef.current = call;

  useEffect(() => { try { sessionStorage.setItem(THREAD_KEY, JSON.stringify(messages.slice(-60))); } catch { /* storage off */ } }, [messages]);
  const push = useCallback((m: Omit<GuideMessage, 'id'>) => setMessages((prev) => [...prev, { ...m, id: uid() }].slice(-80)), []);

  const register = useCallback((reg: PageRegistration) => {
    regs.current = [...regs.current.filter((r) => r !== reg && r.id !== reg.id), reg];
    return () => { regs.current = regs.current.filter((r) => r !== reg); };
  }, []);

  /** Where the person is: the map entry for the URL plus what the page reported. */
  const here = useCallback((): GuideHereWire => {
    const loc = locRef.current;
    const p = parseAppPath(loc.pathname);
    const entry = entryFor(loc.pathname, p.currentPage, p.subTab);
    const reg = [...regs.current].reverse().find((r) => !entry || r.id === entry.id) || null;
    return {
      page: entry?.id || p.currentPage, label: entry?.label || p.currentPage, path: `${loc.pathname}${loc.search}`,
      context: { ...(p.campaignId ? { campaignId: p.campaignId } : {}), ...(reg?.context && typeof reg.context === 'object' ? reg.context as object : reg?.context ? { value: reg.context } : {}), screen: readScreen() },
      actions: reg?.actions?.map(({ name, does, args, confirm }) => ({ name, does, args, confirm })),
    };
  }, []);

  const stopSpeaking = useCallback(() => {
    voiceChain.current = Promise.resolve();
    try { audio.current?.pause(); } catch { /* noop */ }
    audio.current = null;
    ears.current?.setMuted(false);
    if (callRef.current === 'speaking') setCall('listening');
  }, []);

  /** Lykke's voice for one line (OpenAI, like every agent; kept in this tab once heard), the call voice as a fallback. */
  const heard = useRef(new Map<string, string>());
  const lineAudio = useCallback(async (text: string): Promise<string | null> => {
    const known = heard.current.get(text);
    if (known) return known;
    try {
      const r = await fetch(`${API_BASE_URL}/v1/guide/say`, { method: 'POST', headers: authHeaders(true), body: JSON.stringify({ text }) });
      if (r.status === 200) { const url = URL.createObjectURL(await r.blob()); heard.current.set(text, url); return url; }
    } catch { /* fall back */ }
    try { const { audioUrl } = await coworkApi.tts.generate('account-manager', text); return coworkApi.tts.audioSrc(audioUrl); } catch { return null; }
  }, []);

  /** Lines play one after another (the page name first, then the answer) — never over each other. */
  const voiceChain = useRef<Promise<void>>(Promise.resolve());
  const speak = useCallback((text: string) => {
    if (!text || callRef.current === 'off') return;
    voiceChain.current = voiceChain.current.then(async () => {
      if ((callRef.current as CallState) === 'off') return;
      try {
        setCall('speaking');
        const src = await lineAudio(text);
        if (!src || (callRef.current as CallState) === 'off') return;
        const a = new Audio(src);
        audio.current = a;
        ears.current?.setMuted(true); // her own voice is not the person's
        await new Promise<void>((resolve) => { a.onended = () => resolve(); a.onerror = () => resolve(); a.onpause = () => resolve(); void a.play().catch(() => resolve()); });
      } catch { /* a missing voice never blocks the conversation */ }
      finally { if (audio.current) { audio.current = null; ears.current?.setMuted(false); } if ((callRef.current as CallState) !== 'off') setCall('listening'); }
    });
  }, [lineAudio]);

  const askConfirm = useCallback((text: string) => new Promise<boolean>((resolve) => {
    setOpen(true);
    setConfirmAsk({ text });
    confirmResolve.current = (yes) => { setConfirmAsk(null); confirmResolve.current = null; resolve(yes); };
    if (callRef.current !== 'off') speak(text);
  }), [speak]);
  const answerConfirm = useCallback((yes: boolean) => { confirmResolve.current?.(yes); }, []);

  const undo = useCallback((path: string) => {
    const [pathname] = path.split('?');
    const p = parseAppPath(pathname);
    const label = entryFor(pathname, p.currentPage, p.subTab)?.label || 'where you were';
    navigate(path);
    push({ role: 'nav', text: label, path, back: true });
  }, [navigate, push]);

  /** One ask, start to finish (including page actions the turn pauses for). */
  const send = useCallback(async (raw: string, via: 'chat' | 'voice' = 'chat', opts: SendOptions = {}): Promise<GuideTurnRecord> => {
    const text = raw.trim();
    const rec: GuideTurnRecord = { ask: text, startedAt: Date.now(), navigations: [], actions: [], timing: {} };
    if (!text) return rec;
    // A spoken yes/no answers the open question.
    if (confirmResolve.current) {
      if (YES.test(text)) { push({ role: 'user', text, via }); confirmResolve.current(true); return rec; }
      if (NO.test(text)) { push({ role: 'user', text, via }); confirmResolve.current(false); return rec; }
      confirmResolve.current(false);
    }
    push({ role: 'user', text, via });
    setBusy(true);
    if (via === 'voice') setCall('thinking');
    const brandId = readActiveBrandId() || undefined;
    const pages = mapFor(viewerRef.current);
    const sid = opts.session || sessionId();
    let body: Parameters<typeof guideTurn>[0] = { sessionId: sid, mode: via, brandId, here: here(), pages, text };
    const t0 = Date.now();
    let spokenLabel: string | null = null;
    try {
      for (let round = 0; round < 4; round++) {
        const pendingTools: Array<{ id: string; action: string; args: Record<string, unknown> }> = [];
        await guideTurn(body, (e: GuideEvent) => {
          if (e.event === 'route') {
            rec.route = { page: e.data.page, p: e.data.p, aboutScreen: e.data.aboutScreen }; rec.timing.routeMs = Date.now() - t0;
            const target = GUIDE_PAGES.find((x) => x.id === e.data.page);
            if (target && e.data.p >= 0.75) setHeading(target.label);
          }
          else if (e.event === 'navigate') {
            // Dry run (tests): a deck opened with a prompt would be built — open the deck page without building.
            const dry = opts.dryRun && e.data.page === 'reports' && !!e.data.inputs?.prompt;
            const to = dry ? pathFor('reports') : pathFor(e.data.page, e.data.params, e.data.inputs);
            const entry = GUIDE_PAGES.find((x) => x.id === e.data.page);
            if (!to || !entry) return;
            if (e.data.page === 'video-studio' && (e.data.inputs?.prompt || e.data.inputs?.media)) {
              try { sessionStorage.setItem(VIDEO_HANDOFF_KEY, JSON.stringify({ ...e.data.inputs, at: Date.now() })); } catch { /* storage off */ }
            }
            const from = `${locRef.current.pathname}${locRef.current.search}`;
            if (to !== from) {
              navigate(to);
              push({ role: 'nav', text: entry.label, path: to, from });
              // In a call, the page name is said the moment the page moves; the answer follows if it adds something.
              if (e.data.by === 'router' && callRef.current !== 'off') { spokenLabel = entry.label; speak(`${entry.label}.`); }
            }
            rec.navigations.push({ page: e.data.page, path: to, by: e.data.by, ms: Date.now() - t0, inputs: e.data.inputs, params: e.data.params });
            if (rec.timing.firstNavMs === undefined) rec.timing.firstNavMs = Date.now() - t0;
          }
          else if (e.event === 'working') setWorking(e.data.tool);
          else if (e.event === 'client_tool') pendingTools.push(e.data);
          else if (e.event === 'say') {
            rec.said = e.data.text; rec.spoken = e.data.spoken;
            if (!e.data.text) return; // nothing to say (a "thanks" gets nothing back)
            push({ role: 'lykke', text: e.data.text });
            const plain = (x: string) => x.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
            const onlyLabel = !!spokenLabel && plain(e.data.spoken).split(' ').length <= plain(spokenLabel).split(' ').length + 2 && plain(e.data.spoken).startsWith(plain(spokenLabel).split(' ')[0]);
            if ((via === 'voice' || callRef.current !== 'off') && !onlyLabel) speak(e.data.spoken);
          }
          else if (e.event === 'error') { rec.error = e.data.message; push({ role: 'error', text: e.data.message }); }
          else if (e.event === 'done') rec.timing.doneMs = Date.now() - t0;
        });
        if (!pendingTools.length) break;
        // The turn paused for the page: run what it asked (asking the person first where the action says so).
        setWorking(null);
        const results: Array<{ id: string; result: unknown }> = [];
        for (const t of pendingTools) {
          const reg = [...regs.current].reverse().find((r) => r.actions?.some((a) => a.name === t.action));
          const action = reg?.actions?.find((a) => a.name === t.action);
          if (!action) { results.push({ id: t.id, result: { ok: false, error: 'That page is not open any more.' } }); rec.actions.push({ action: t.action, ok: false }); continue; }
          if (action.confirm) {
            const question = action.confirmText?.(t.args) || `${action.does}?`;
            (rec.confirms ||= []).push(question);
            const yes = opts.declineConfirms ? false : await askConfirm(question);
            if (!yes) { results.push({ id: t.id, result: { ok: false, declined: true, note: 'The person said no. Do not try again.' } }); rec.actions.push({ action: t.action, ok: false }); continue; }
          }
          try {
            const out = await action.run(t.args);
            results.push({ id: t.id, result: { ok: true, result: out ?? null, screen: reg?.context ?? null } });
            rec.actions.push({ action: t.action, ok: true });
          } catch (err) {
            results.push({ id: t.id, result: { ok: false, error: (err as Error).message.slice(0, 200) } });
            rec.actions.push({ action: t.action, ok: false });
          }
        }
        body = { sessionId: sid, mode: via, brandId, here: here(), pages, toolResults: results };
      }
    } catch (err) {
      rec.error = (err as Error).message;
      push({ role: 'error', text: 'That did not go through. Try again.' });
    } finally {
      setBusy(false); setWorking(null); setHeading(null);
      if (callRef.current === 'thinking') setCall('listening');
      setLastTurn(rec);
    }
    return rec;
  }, [askConfirm, here, navigate, push, speak]);

  /** Asks never get lost: one typed or said while Lykke is still on the last one waits its turn. */
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const sendQueued = useCallback((text: string, via: 'chat' | 'voice' = 'chat', opts: SendOptions = {}): Promise<GuideTurnRecord> => {
    // An answer to the open question ("yes", "no", or anything that replaces it) goes straight through: the turn
    // waiting for it is the one ahead in the queue.
    if (confirmResolve.current) return send(text, via, opts);
    const run = chain.current.then(() => send(text, via, opts), () => send(text, via, opts));
    chain.current = run.catch(() => undefined);
    return run;
  }, [send]);
  const sendRef = useRef(sendQueued);
  sendRef.current = sendQueued;

  const endCall = useCallback(() => {
    ears.current?.end(); ears.current = null;
    try { audio.current?.pause(); } catch { /* noop */ }
    audio.current = null;
    setCall('off');
  }, []);

  const startCall = useCallback(async () => {
    if (callRef.current !== 'off') return;
    setOpen(true);
    setCall('connecting');
    try {
      const e = new RealtimeEars({
        onUtterance: (t, final) => { if (final && t.trim().length > 1) void sendRef.current(t, 'voice'); },
        onSpeechStart: () => { if (audio.current) stopSpeaking(); },
        onError: (m) => push({ role: 'error', text: m }),
        onEnded: () => { if (ears.current === e) ears.current = null; setCall('off'); },
        // The call's minutes ran out (or the server ended it): a calm line in the thread, the ears stop.
        onTimeUp: (m) => push({ role: 'lykke', text: m }),
      });
      ears.current = e;
      // The offer goes to the mint route: the server connects the ears and keeps their voice session (voiceSession.ts).
      await e.connect((sdp) => coworkApi.calls.earsSecret({ sdp }));
      if (!e.active) return;
      setCall('listening');
      // Page names are said the moment a page opens: have them ready (in the background, one at a time).
      void (async () => { for (const p of mapFor(viewerRef.current)) { if (callRef.current === 'off') break; await lineAudio(`${p.label}.`); } })();
    } catch (err) {
      ears.current = null;
      setCall('off');
      push({ role: 'error', text: voiceRefusalText(err) ?? (/permission|denied|NotAllowed/i.test(String(err)) ? 'The microphone is blocked for this site.' : 'The call could not start.') });
    }
  }, [push, stopSpeaking, lineAudio]);

  useEffect(() => () => { ears.current?.end(); }, []);

  const value = useMemo<GuideValue>(() => ({
    open, setOpen, messages, busy, working, heading, send: sendQueued, call, startCall, endCall, stopSpeaking, confirmAsk, answerConfirm, undo, register, available: enabled, lastTurn,
  }), [open, messages, busy, working, heading, sendQueued, call, startCall, endCall, stopSpeaking, confirmAsk, answerConfirm, undo, register, enabled, lastTurn]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useGuide(): GuideValue | null { return useContext(Ctx); }

/**
 * A page tells the guide what is on screen and what it can do. Call it with the page's map id; the context and
 * actions are read at the moment of each ask, so pass the current values (they are kept in a ref).
 */
export function useAgentPage(id: string, context?: unknown, actions?: GuideAction[]) {
  const g = useContext(Ctx);
  const reg = useRef<PageRegistration>({ id, context, actions });
  reg.current.id = id; reg.current.context = context; reg.current.actions = actions;
  useEffect(() => g?.register(reg.current), [g, id]);
}
